/**
 * Dashboard session: the bearer token minted by /auth/login (routes/session.js).
 *
 * Until Sep 2026 the dashboard kept "email:password" in localStorage and
 * re-posted it on every load. Now it keeps the session token and sends it as
 * `Authorization: Bearer` on every same-origin /api and /auth call, which is
 * what the server's route guards look for.
 *
 * The map client (client-mpathic) runs in an iframe on the same origin and
 * reads the same storage, and the mobile app opens the /view/.../mobile page
 * with its own token in the url fragment (`#session=<token>`): the fragment is
 * never sent to the server or written to logs, and it is moved into
 * sessionStorage on load so a reload of the page keeps working.
 */
const TOKEN_KEY = 'mpath.session'
const FRAGMENT_PARAM = 'session'

const storage = (kind) => {
  try {
    return window[kind]
  } catch (e) {
    return null
  }
}

export const adoptFragmentToken = () => {
  try {
    const hash = window.location.hash || ''
    if (hash.indexOf(FRAGMENT_PARAM + '=') === -1) return
    const params = new URLSearchParams(hash.replace(/^#/, ''))
    const token = params.get(FRAGMENT_PARAM)
    if (!token) return
    const session = storage('sessionStorage')
    if (session) session.setItem(TOKEN_KEY, token)
    params.delete(FRAGMENT_PARAM)
    const rest = params.toString()
    window.history.replaceState(
      null,
      '',
      window.location.pathname +
        window.location.search +
        (rest ? '#' + rest : ''),
    )
  } catch (e) {
    // a browser that refuses history or storage still gets the page
  }
}

export const getSessionToken = () => {
  const session = storage('sessionStorage')
  const fromSession = session ? session.getItem(TOKEN_KEY) : null
  if (fromSession) return fromSession
  const local = storage('localStorage')
  return local ? local.getItem(TOKEN_KEY) : null
}

export const setSessionToken = (token) => {
  const local = storage('localStorage')
  if (local) local.setItem(TOKEN_KEY, token)
}

export const clearSessionToken = () => {
  const local = storage('localStorage')
  if (local) local.removeItem(TOKEN_KEY)
  const session = storage('sessionStorage')
  if (session) session.removeItem(TOKEN_KEY)
}

const sameOriginApiUrl = (input) => {
  const url = typeof input === 'string' ? input : input && input.url
  if (!url) return false
  if (url.startsWith('/api/') || url.startsWith('/auth/')) return true
  try {
    const parsed = new URL(url, window.location.origin)
    return (
      parsed.origin === window.location.origin &&
      (parsed.pathname.startsWith('/api/') ||
        parsed.pathname.startsWith('/auth/'))
    )
  } catch (e) {
    return false
  }
}

/**
 * Wraps window.fetch once so every existing call site (there are dozens, in
 * two bundles) carries the session without being touched.
 */
export const installAuthFetch = () => {
  if (
    typeof window === 'undefined' ||
    !window.fetch ||
    window.fetch.__mpathSession
  )
    return
  adoptFragmentToken()
  const original = window.fetch.bind(window)
  const wrapped = (input, init) => {
    const token = getSessionToken()
    if (!token || !sameOriginApiUrl(input)) return original(input, init)
    const options = Object.assign({}, init)
    const headers = new Headers(
      (init && init.headers) || (input && input.headers) || undefined,
    )
    if (!headers.has('Authorization'))
      headers.set('Authorization', 'Bearer ' + token)
    options.headers = headers
    return original(input, options)
  }
  wrapped.__mpathSession = true
  window.fetch = wrapped
}

/** Ends the server session too, then forgets the token. */
export const signOut = () => {
  const token = getSessionToken()
  const done = () => clearSessionToken()
  if (!token) return Promise.resolve(done())
  return fetch('/auth/logout', { method: 'POST' })
    .catch(() => {})
    .then(done)
}

/**
 * Asks the server to sign an OAuth start state for this session
 * (`POST /auth/oauth/state`, routes/auth.js), the same handshake the mobile
 * app uses (mpath-app src/screens/connect/oauth.ts).
 *
 * The dashboard used to build `<project>---<email>---<customdata>` itself and
 * put it straight in the authorize URL. That is a top-level navigation, so it
 * carries no Authorization header and the server could only take the state on
 * trust - anyone could craft a link that attached their provider account to
 * another user's record. The server accepts unsigned state only while
 * LEGACY_UNSIGNED_OAUTH_STATE is on, which comes off for the CASA assessment.
 *
 * `actingUser` is for an admin starting the flow on behalf of a participant
 * (the Reauthorize button): requireUser resolves a named user for an admin
 * session, so the state is signed for that participant.
 *
 * Returns null when the server refuses, and the caller should not navigate.
 */
export const requestOauthState = (project, customdata, actingUser) => {
  const query = actingUser ? '?user=' + encodeURIComponent(actingUser) : ''
  return window
    .fetch('/auth/oauth/state' + query, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        project: project || 'global',
        customdata: customdata === undefined ? '' : customdata,
      }),
    })
    .then((r) => r.json())
    .then((d) => (d && d.message === 'success' && d.state ? d.state : null))
    .catch(() => null)
}
